import React, { useMemo, useState } from 'react';
import { Tabs, Table, Button, Space, Typography, Modal, message, Card, Breadcrumb, Empty } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import {
    RECOMMEND_FUNNELS, RECOMMEND_STRATEGIES, RECOMMEND_TEMPLATES
} from './recommendMockData';
import RecommendSlotTree from './components/RecommendSlotTree';
import RecommendFunnelDrawer from './components/RecommendFunnelDrawer';
import RecommendStrategyDrawer from './components/RecommendStrategyDrawer';

const { Title } = Typography;

const formatDate = (ts) => (ts ? new Date(ts * 1000).toLocaleDateString('zh-CN') : '-');

// ══════════════════════════════════════════════
//  推荐位运营（推荐位套餐漏斗 / 推荐位策略）
//  真实项目：views/operation/recommended/index.vue
// ══════════════════════════════════════════════

const RecommendOperation = () => {
    const [activeTab, setActiveTab] = useState('slot');
    const [currentSlot, setCurrentSlot] = useState(null);
    const [funnels, setFunnels] = useState(RECOMMEND_FUNNELS);
    const [strategies, setStrategies] = useState(RECOMMEND_STRATEGIES);
    const [templates, setTemplates] = useState(RECOMMEND_TEMPLATES);
    const [funnelDrawer, setFunnelDrawer] = useState({ open: false, editing: null });
    const [strategyDrawer, setStrategyDrawer] = useState({ open: false, editing: null });

    const slotFunnels = useMemo(() => funnels.filter(f => f.slotCode === currentSlot?.slotCode), [funnels, currentSlot]);
    const slotStrategies = useMemo(() => strategies.filter(s => s.slotCode === currentSlot?.slotCode), [strategies, currentSlot]);

    // ── 漏斗 ──
    const handleFunnelSave = (data) => {
        if (data.id) {
            setFunnels(prev => prev.map(f => (f.id === data.id ? { ...f, ...data } : f)));
        } else {
            setFunnels(prev => [...prev, { ...data, id: Date.now(), createdAt: Math.floor(Date.now() / 1000) }]);
        }
        setFunnelDrawer({ open: false, editing: null });
    };

    const handleFunnelDelete = (record) => {
        Modal.confirm({
            title: '删除确认', content: `确定要删除漏斗"${record.name}"吗？`,
            okText: '确定', cancelText: '取消', okButtonProps: { danger: true },
            onOk: () => { setFunnels(prev => prev.filter(f => f.id !== record.id)); message.success('删除成功'); },
        });
    };

    // ── 策略 ──
    const handleStrategySave = (data) => {
        if (data.id) {
            setStrategies(prev => prev.map(s => (s.id === data.id ? { ...s, ...data } : s)));
        } else {
            setStrategies(prev => [...prev, { ...data, id: Date.now(), isDefault: 0, createdAt: Math.floor(Date.now() / 1000) }]);
        }
        setStrategyDrawer({ open: false, editing: null });
    };

    const handleStrategyDelete = (record) => {
        Modal.confirm({
            title: '删除确认', content: `确定要删除策略"${record.name}"吗？`,
            okText: '确定', cancelText: '取消', okButtonProps: { danger: true },
            onOk: () => { setStrategies(prev => prev.filter(s => s.id !== record.id)); message.success('删除成功'); },
        });
    };

    const funnelColumns = [
        { title: '漏斗名称', dataIndex: 'name', key: 'name', width: 200, render: (v) => <span style={{ fontWeight: 600 }}>{v}</span> },
        { title: '创建时间', key: 'createdAt', width: 200, align: 'center', render: (_, r) => formatDate(r.createdAt) },
        {
            title: '操作', key: 'action', width: 150, align: 'center',
            render: (_, record) => (
                <Space size={12}>
                    <Button type="link" size="small" icon={<EditOutlined />} onClick={() => setFunnelDrawer({ open: true, editing: record })}>编辑</Button>
                    <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => handleFunnelDelete(record)}>删除</Button>
                </Space>
            ),
        },
    ];

    const strategyColumns = [
        { title: '策略名称', dataIndex: 'name', key: 'name', width: 200, render: (v) => <span style={{ fontWeight: 600 }}>{v}</span> },
        { title: '创建时间', key: 'createdAt', width: 200, align: 'center', render: (_, r) => formatDate(r.createdAt) },
        { title: '冷却期', key: 'cooldownDays', width: 90, align: 'center', render: (_, r) => (r.cooldownDays ? `${r.cooldownDays}天` : '-') },
        {
            title: '操作', key: 'action', width: 150, align: 'center',
            render: (_, record) => (
                <Space size={12}>
                    <Button type="link" size="small" icon={<EditOutlined />} onClick={() => setStrategyDrawer({ open: true, editing: record })}>编辑</Button>
                    {record.isDefault !== 1 && (
                        <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => handleStrategyDelete(record)}>删除</Button>
                    )}
                </Space>
            ),
        },
    ];

    const renderContent = () => {
        if (!currentSlot) return <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}><Empty description="请在左侧选择推荐位" /></div>;
        const isFunnel = activeTab === 'slot';
        const title = isFunnel ? `${currentSlot.slotName} · 漏斗列表` : `${currentSlot.slotName} · 策略列表`;
        return (
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <div style={{ padding: '14px 20px', borderBottom: '1px solid #ebeef5', flexShrink: 0 }}>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#303133' }}>{title}</h3>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                        {isFunnel
                            ? <Button type="primary" icon={<PlusOutlined />} onClick={() => setFunnelDrawer({ open: true, editing: null })}>添加漏斗</Button>
                            : <Button type="primary" icon={<PlusOutlined />} onClick={() => setStrategyDrawer({ open: true, editing: null })}>添加策略</Button>}
                    </div>
                    <Table
                        dataSource={isFunnel ? slotFunnels : slotStrategies}
                        columns={isFunnel ? funnelColumns : strategyColumns}
                        rowKey="id"
                        bordered={false}
                        pagination={false}
                    />
                </div>
            </div>
        );
    };

    return (
        <div style={{ padding: 24, background: '#f5f6f8', minHeight: '100vh' }}>
            <Breadcrumb style={{ marginBottom: 16 }} items={[{ title: '运营管理' }, { title: '推荐位运营' }]} />
            <Title level={4} style={{ marginBottom: 16 }}>推荐位运营</Title>

            <Card styles={{ body: { display: 'flex', flexDirection: 'column' } }}>
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={[{ key: 'slot', label: '推荐位套餐漏斗' }, { key: 'strategy', label: '推荐位策略' }]}
                />
                <div style={{ display: 'flex', height: 560, marginTop: 16, border: '1px solid #f0f0f0', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ width: 220, flexShrink: 0 }}>
                        <RecommendSlotTree onChange={setCurrentSlot} />
                    </div>
                    {renderContent()}
                </div>
            </Card>

            <RecommendFunnelDrawer
                open={funnelDrawer.open}
                editData={funnelDrawer.editing}
                slotCode={currentSlot?.slotCode}
                appId={currentSlot?.appId}
                onCancel={() => setFunnelDrawer({ open: false, editing: null })}
                onSuccess={handleFunnelSave}
            />

            <RecommendStrategyDrawer
                open={strategyDrawer.open}
                slot={currentSlot}
                editData={strategyDrawer.editing}
                funnels={funnels}
                templates={templates}
                onCancel={() => setStrategyDrawer({ open: false, editing: null })}
                onSuccess={handleStrategySave}
                onSwitchTab={setActiveTab}
                onTemplatesChange={setTemplates}
            />
        </div>
    );
};

export default RecommendOperation;
